import {
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  Line,
  ShaderMaterial,
  AdditiveBlending,
  Quaternion,
  MathUtils
} from 'three';

import { sceneManager} from '../script.js';

import magneticVertex from '../../../shaders/magnetic/vertexMagnetic.glsl';
import magneticFragment from '../../../shaders/magnetic/fragmentMagnetic.glsl';

export function createLineShaderMaterial() {
  return new ShaderMaterial({
    uniforms: { time: { value: 0.0 } },
    vertexShader: magneticVertex,
    fragmentShader: magneticFragment,
    transparent: true,
    blending: AdditiveBlending,
    depthTest: false,
    depthWrite: false
  });
}
function removeExistingMagneticFieldLines(mesh) {
  mesh.children
    .filter(child => child.userData?.isMagneticFieldLine)
    .forEach(child => mesh.remove(child));
}

export function createCommonMagneticField(object) {
  removeExistingMagneticFieldLines(object.mesh);

  const lines = [];
  const R = object.size;
  const segments = object.desiredMinRmax * object.totalPhi;
  const lineMaterial = createLineShaderMaterial();
  lineMaterial.uniforms.fadeSet = { value: false };
  lineMaterial.transparent = true;

  object.currentRepulsionForce = sceneManager.commands.controls.windSun * object.amortMag;

  for (let i = 0; i < object.totalPhi; i++) {
    const phi = (i / object.totalPhi) * Math.PI * 2;

    for (let j = 0; j < object.totalRings; j++) {
      const rmax = R * object.desiredMaxRmax +
                   (R * object.desiredMinRmax - R * object.desiredMaxRmax) *
                   (j / (object.totalRings - 1));
      const theta0 = Math.asin(Math.sqrt(R / rmax));

      const line = createMagneticFieldLine(R, theta0, phi, segments, lineMaterial);
      line.renderOrder = 2
      object.mesh.add(line);
      lines.push(line);
    }
  }
  return lines;
}
function createMagneticFieldLine(planetRadius, theta0, phi, segments, material) {
  const randomScale = 0.8 + Math.random() * 0.4;
  const points = [], uvs = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const theta = theta0 + (Math.PI - 2 * theta0) * t;
    const r = planetRadius * Math.sin(theta)**2 / Math.sin(theta0)**2 * randomScale;
    const x = r * Math.sin(theta) * Math.cos(phi);
    const y = r * Math.cos(theta);
    const z = r * Math.sin(theta) * Math.sin(phi);

    points.push(new Vector3(x, y, z));
    uvs.push(t, 0);
  }
  const geometry = new BufferGeometry().setFromPoints(points);
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));

  const line = new Line(geometry, material.clone());

  line.visible = sceneManager.commands.controls.magnetosphere;

  
  line.userData = { isMagneticFieldLine: true, originalPoints: points };
  return line;
}

export function updateCommonMagneticField(newObject, lines) {
  lines.forEach(line => {
    if (line.parent) line.parent.remove(line);
    newObject.mesh.add(line);
  });

  const needed = newObject.totalPhi * newObject.totalRings;
  if (lines.length !== needed) {
    lines.forEach(l => newObject.mesh.remove(l));
    return createCommonMagneticField(newObject);
  }
  const lerp = 0.05;
  newObject.currentRepulsionForce +=
    (sceneManager.commands.controls.windSun * newObject.amortMag - newObject.currentRepulsionForce) * lerp;

  _updateFieldLines(newObject, lines);
  return lines;
}

export function updateMagneticFieldAnimation(object, lines) {
  const lerp = 0.05;
  object.currentRepulsionForce +=
    (sceneManager.commands.controls.windSun * object.amortMag - object.currentRepulsionForce) * lerp;

  _updateFieldLines(object, lines);
}

function _updateFieldLines(object, lines) {
  const objPos = new Vector3(), sunPos = new Vector3(0,0,0);
  const tmpW = new Vector3(), tmpL = new Vector3();
  const quat = new Quaternion(), invQuat = new Quaternion();

  object.mesh.getWorldPosition(objPos);
  object.mesh.getWorldQuaternion(quat);
  invQuat.copy(quat).invert();

  const extraTilt = new Quaternion().setFromAxisAngle(
    new Vector3(1, 0, 0),
    MathUtils.degToRad(object.inclinMag)
  );

  lines.forEach(line => {

    const posAttr = line.geometry.attributes.position;
    const arr = posAttr.array;
    const orig = line.userData.originalPoints;

    for (let i = 0; i < orig.length; i++) {
      tmpL.copy(orig[i]).applyQuaternion(extraTilt);

      const worldBase = object.mesh.localToWorld(tmpL.clone());
      const windW = tmpW.subVectors(worldBase, sunPos).normalize();
      const windL = windW.clone().applyQuaternion(invQuat).normalize();

      const dist = orig[i].length();
      const expF = 11;
      const factor = (Math.exp(expF * (dist / (object.size * object.desiredMaxRmax))) - 1)
                   / (Math.exp(expF) - 1);
      const d = worldBase.distanceTo(sunPos);
      const decay = 1 / (d * d);

      const latAxis = Math.abs(windL.y) > 0.99 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
      const latDir = new Vector3().crossVectors(windL, latAxis).normalize();
      const osc = Math.sin(Date.now() * 0.001 * object.twistFrequency);
      windL.add(latDir.multiplyScalar(object.lateralTwist * osc)).normalize();

      let newP = tmpL.clone().add(
        windL.multiplyScalar(object.currentRepulsionForce * factor * decay)
      );

      arr[i * 3]     = newP.x;
      arr[i * 3 + 1] = newP.y;
      arr[i * 3 + 2] = newP.z;
    }
    
    posAttr.needsUpdate = true;
    line.material.uniforms.time.value = performance.now() * 0.0005;
    line.visible = sceneManager.commands.controls.magnetosphere;
  });
}
